import React, { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";

const EMBLEM = `${process.env.PUBLIC_URL || ""}/htc-emblem.png`;

export const Intro = ({ onDone }) => {
  const [exiting, setExiting] = useState(false);
  const [showEnter, setShowEnter] = useState(false);

  const finish = useCallback(() => {
    setExiting(true);
    setTimeout(onDone, 1100);
  }, [onDone]);

  useEffect(() => {
    const tEnter = setTimeout(() => setShowEnter(true), 3000);
    const tAuto = setTimeout(finish, 6500);
    return () => {
      clearTimeout(tEnter);
      clearTimeout(tAuto);
    };
  }, [finish]);

  const ease = [0.22, 1, 0.36, 1];

  return (
    <motion.div
      className="fixed inset-0 z-[120] bg-ink flex flex-col items-center justify-center overflow-hidden px-6"
      animate={{ y: exiting ? "-100%" : "0%" }}
      transition={{ duration: 1, ease: [0.76, 0, 0.24, 1] }}
      data-testid="intro-overlay"
    >
      {/* faint radial glow */}
      <motion.div
        className="pointer-events-none absolute inset-0"
        style={{
          background:
            "radial-gradient(circle at 50% 42%, rgba(200,161,90,0.12) 0%, rgba(17,17,17,0) 55%)",
        }}
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 2, ease }}
      />

      <div className="relative z-10 flex flex-col items-center text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.82, filter: "blur(6px)" }}
          animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
          transition={{ duration: 1.4, ease }}
        >
          <motion.img
            src={EMBLEM}
            alt="Houston Title Co. emblem"
            className="h-24 md:h-32 w-auto"
            draggable={false}
            animate={{ y: [0, -8, 0] }}
            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
            data-testid="intro-emblem"
          />
        </motion.div>

        <motion.div
          className="mt-10 mb-8 h-px bg-gold/70"
          initial={{ width: 0 }}
          animate={{ width: 120 }}
          transition={{ duration: 1.2, delay: 0.6, ease }}
        />

        <motion.div
          className="overline mb-7"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1, delay: 1 }}
        >
          Houston, Texas
        </motion.div>

        <div className="font-serif font-light text-ivory leading-[0.98] text-4xl sm:text-5xl md:text-6xl tracking-tight">
          {["A New Title", "Experience"].map((line, i) => (
            <div key={i} className="overflow-hidden">
              <motion.div
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ duration: 1.1, delay: 1.2 + i * 0.14, ease: [0.16, 1, 0.3, 1] }}
              >
                {line}
              </motion.div>
            </div>
          ))}
        </div>

        <motion.p
          className="font-serif italic text-2xl md:text-3xl mt-6"
          initial={{ opacity: 0, y: 14 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1, delay: 1.9, ease }}
        >
          <span className="shimmer">Coming Soon</span>
        </motion.p>

        <motion.p
          className="mt-10 max-w-md text-taupe font-light text-sm md:text-base leading-relaxed"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1.2, delay: 2.3 }}
        >
          Houston Title Co. is completing licensing to bring a private, precise standard of title &
          escrow to Texas. We invite you inside for an early look.
        </motion.p>

        <AnimatePresence>
          {showEnter && (
            <motion.button
              onClick={finish}
              data-testid="intro-enter-btn"
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8, ease }}
              className="group relative mt-12 overflow-hidden border border-gold/60 text-gold text-[0.72rem] uppercase tracking-[0.24em] px-10 py-4"
            >
              <span className="relative z-10 transition-colors duration-500 group-hover:text-ink">
                Enter
              </span>
              <span className="absolute inset-0 bg-gold translate-y-full transition-transform duration-500 group-hover:translate-y-0" />
            </motion.button>
          )}
        </AnimatePresence>
      </div>

      <motion.div
        className="absolute bottom-8 overline text-[0.55rem] text-taupe/50"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 2.6 }}
      >
        Experience · Integrity · Results
      </motion.div>
    </motion.div>
  );
};
